import { createClient } from '../client/browser'
import type { SupabaseClient } from '@supabase/supabase-js'

export interface MatchingCriteria {
  sessionType: 'individual' | 'couples'
  therapistTier: 'resident' | 'associate'
  rate: number
  timezone: string
  availability: string[]
  insurance: string[]
}

// Return type is now just an array of pro IDs

/**
 * Find matching pros based on quiz criteria
 */
export async function findMatchingPros(
  criteria: MatchingCriteria,
  supabase?: SupabaseClient
): Promise<{ proIds: string[]; error?: string }> {
  const client = supabase || createClient()
  
  try {
    // Start with base query for active therapists
    let query = client
      .from('pros')
      .select(`
        id,
        timezone,
        schedule,
        credentials,
        matched
      `)
      .eq('isActive', true)
      .eq('isTherapist', true)
    
    // Filter by session type - only fetch pros who accept this type
    if (criteria.sessionType === 'couples') {
      query = query.eq('isAcceptingCouples', true)
    } else {
      query = query.eq('isAcceptingIndividual', true)
    }
    
    // Filter by therapist tier
    if (criteria.therapistTier === 'resident') {
      // Resident tier MUST be RESIDENT employment status. Cohorts open
      // automatically once their startDate has passed — no code edit
      // needed per cohort. Admin can still gate individual pros (or an
      // entire cohort in bulk) via the isAcceptingIndividual/Couples
      // flags managed at /admin/cohorts.
      const today = new Date().toISOString().slice(0, 10)
      query = query.eq('employmentStatus', 'RESIDENT').lte('startDate', today)
    } else {
      // Associate tier - can be EMPLOYEE or CONTRACTOR
      // Check if ONLY RSW insurance is selected
      const hasOnlyRSWInsurance = criteria.insurance.length === 1 && criteria.insurance[0] === 'rsw'
      
      if (hasOnlyRSWInsurance) {
        // ONLY RSW insurance requires CONTRACTOR with RSW credentials
        query = query.eq('employmentStatus', 'CONTRACTOR')
      } else {
        // All other insurance combinations - EMPLOYEE only
        query = query.eq('employmentStatus', 'EMPLOYEE')
      }
    }
    
    // Execute query
    console.log('Executing pro query...')
    const { data: pros, error } = await query
    
    if (error) {
      console.error('Error finding matching pros:', error)
      return { proIds: [], error: 'Failed to find matching therapists' }
    }
    
    console.log('Found pros:', pros?.length || 0)
    
    if (!pros || pros.length === 0) {
      console.log('No pros found with current criteria')
      return { proIds: [], error: 'No matching therapists found' }
    }
    
    // Additional filtering for RSW credentials if needed
    let filteredPros = pros
    if (criteria.therapistTier === 'associate' && 
        criteria.insurance.length === 1 && 
        criteria.insurance[0] === 'rsw') {
      filteredPros = pros.filter(pro => {
        if (!pro.credentials || typeof pro.credentials !== 'object') return false
        // Check if credentials contain RSW
        const credentialsStr = JSON.stringify(pro.credentials).toLowerCase()
        return credentialsStr.includes('rsw') || credentialsStr.includes('registered social worker')
      })
    }
    
    // Score and sort pros based on matching criteria
    const scoredPros = filteredPros.map(pro => {
      let score = 0
      
      // Availability match with timezone adjustment
      if (pro.schedule) {
        const availabilityScore = calculateAvailabilityMatch(
          pro.schedule,
          criteria.availability,
          criteria.timezone,
          pro.timezone
        )
        score += availabilityScore
      }
      
      // Fairness score - strongly prioritize pros who have been matched less
      // Use exponential decay to heavily penalize over-matched therapists
      const matchedCount = pro.matched || 0
      let fairnessScore = 0
      
      if (matchedCount < 50) {
        fairnessScore = 100 // Huge bonus for new therapists
      } else if (matchedCount < 100) {
        fairnessScore = 50 // Good bonus for less-matched therapists
      } else if (matchedCount < 150) {
        fairnessScore = 20 // Small bonus
      } else if (matchedCount < 200) {
        fairnessScore = 0 // Neutral
      } else {
        // Exponential penalty for over-matched therapists
        fairnessScore = -Math.min(100, (matchedCount - 200) * 0.5)
      }
      
      score += fairnessScore
      
      return { 
        id: pro.id,
        score,
        matchedCount
      }
    })
    
    // Add small random factor to scores to break ties and distribute matches
    const prosWithRandomization = scoredPros.map(pro => ({
      ...pro,
      // Add random value between -5 and 5 to help distribute similar scores
      finalScore: pro.score + (Math.random() * 10 - 5)
    }))
    
    // Sort by final score (highest first) and limit to top 4 matches
    const topMatches = prosWithRandomization
      .sort((a, b) => b.finalScore - a.finalScore)
      .slice(0, 4) // Return top 4 matches (or fewer if less than 4 available)
    
    // Log matching details for debugging
    console.log(`Top ${topMatches.length} matches:`, topMatches.map(m => ({
      id: m.id,
      matchedCount: m.matchedCount,
      score: m.score,
      finalScore: m.finalScore.toFixed(2)
    })))
    
    const sortedProIds = topMatches.map(pro => pro.id) // Extract just the IDs
    
    return { proIds: sortedProIds }
    
  } catch (error) {
    console.error('Unexpected error in findMatchingPros:', error)
    return { proIds: [], error: 'An unexpected error occurred' }
  }
}

/**
 * Calculate availability match score with timezone adjustment
 */
function calculateAvailabilityMatch(
  proSchedule: any,
  userPreferences: string[],
  userTimezone: string,
  proTimezone: string | null
): number {
  let score = 0
  
  if (!proSchedule || typeof proSchedule !== 'object') {
    return 0
  }
  
  // Count total availability slots to detect "all available" therapists
  const totalSlots = Object.keys(proSchedule).filter(k => proSchedule[k] === true).length
  const hasAllSlotsAvailable = totalSlots >= 7 // 7 days = likely default data
  
  // Get timezone offset difference in hours
  const timezoneOffsetHours = getTimezoneOffsetHours(userTimezone, proTimezone)
  
  // Check each user preference against pro's schedule
  userPreferences.forEach(preference => {
    // Handle special case for 'flexible' availability
    if (preference === 'flexible') {
      // Give a moderate score for flexible availability
      score += 3
      return
    }
    
    const parts = preference.split('-')
    if (parts.length !== 2) {
      console.warn(`Invalid availability preference format: ${preference}`)
      return
    }
    
    const [dayType, timeOfDay] = parts
    
    // Adjust time of day based on timezone difference
    const adjustedTimeOfDay = timeOfDay ? adjustTimeOfDay(timeOfDay, timezoneOffsetHours) : 'morning'
    
    // Check if pro has availability for this adjusted preference
    const availKey = `${dayType}${adjustedTimeOfDay.charAt(0).toUpperCase() + adjustedTimeOfDay.slice(1)}`
    
    if (proSchedule[availKey] === true) {
      // Give lower score if therapist has "all days available" (likely default data)
      score += hasAllSlotsAvailable ? 2 : 5
    }
  })
  
  return score
}

/**
 * Get timezone offset difference in hours
 */
function getTimezoneOffsetHours(userTimezone: string, proTimezone: string | null): number {
  if (!proTimezone) return 0
  
  // Map IANA timezones to UTC offsets (simplified for Canadian timezones)
  const timezoneOffsets: { [key: string]: number } = {
    'America/Vancouver': -8,   // PST
    'America/Edmonton': -7,    // MST
    'America/Regina': -6,      // CST (no DST)
    'America/Winnipeg': -6,    // CST
    'America/Toronto': -5,     // EST
    'America/Halifax': -4,     // AST
    'America/St_Johns': -3.5   // NST
  }
  
  const userOffset = timezoneOffsets[userTimezone] || -5
  const proOffset = timezoneOffsets[proTimezone] || -5
  
  return proOffset - userOffset
}

/**
 * Adjust time of day based on timezone offset
 */
function adjustTimeOfDay(timeOfDay: string, offsetHours: number): string {
  // Map time periods to hour ranges
  const timePeriods = {
    morning: { start: 6, end: 12 },
    afternoon: { start: 12, end: 17 },
    evening: { start: 17, end: 23 }
  }
  
  const period = timePeriods[timeOfDay as keyof typeof timePeriods]
  if (!period) return timeOfDay
  
  // Adjust the time range by offset
  const adjustedStart = period.start + offsetHours
  
  // Determine which time period the adjusted time falls into
  if (adjustedStart >= 6 && adjustedStart < 12) return 'morning'
  if (adjustedStart >= 12 && adjustedStart < 17) return 'afternoon'
  if (adjustedStart >= 17 && adjustedStart < 23) return 'evening'
  
  // Handle edge cases (very early or very late)
  if (adjustedStart < 6) return 'evening' // Previous day's evening
  if (adjustedStart >= 23) return 'morning' // Next day's morning
  
  return timeOfDay
}

/**
 * Find and persist matches for a freshly-inserted matching_quizzes row.
 * Used by both authenticated submit (saveQuizData) and guest submit (/api/quiz/temp).
 */
export async function runMatching(
  quiz: {
    id: string
    type: string | null
    tier: string | null
    rate: number | null
    timezone: string | null
    times: string[]
    insurance: string[]
  },
  supabase?: SupabaseClient,
): Promise<{ proIds: string[]; error?: string }> {
  const client = supabase || createClient()

  if (!quiz.type || !quiz.tier || quiz.rate == null || !quiz.timezone) {
    return { proIds: [], error: 'Quiz missing required matching fields' }
  }

  const { proIds, error: matchingError } = await findMatchingPros(
    {
      sessionType: quiz.type as 'individual' | 'couples',
      therapistTier: quiz.tier as 'resident' | 'associate',
      rate: quiz.rate,
      timezone: quiz.timezone,
      availability: quiz.times,
      insurance: quiz.insurance,
    },
    client,
  )

  if (matchingError) {
    return { proIds: [], error: matchingError }
  }

  if (proIds.length > 0) {
    await updateMatchingQuizWithPros(quiz.id, proIds, client)
  }

  return { proIds }
}

/**
 * Update matching quiz with matched pro IDs
 */
export async function updateMatchingQuizWithPros(
  quizId: string,
  proIds: string[],
  supabase?: SupabaseClient
): Promise<{ success: boolean; error?: string }> {
  const client = supabase || createClient()
  
  try {
    const { error } = await client
      .from('matching_quizzes')
      .update({
        matchedProIds: proIds,
        status: 'matched',
        updatedAt: new Date().toISOString()
      })
      .eq('id', quizId)
    
    if (error) {
      console.error('Error updating matching quiz:', error)
      return { success: false, error: 'Failed to update matching results' }
    }
    
    // Increment matched count for all matched pros
    for (const proId of proIds) {
      // First get current matched count
      const { data: proData, error: fetchError } = await client
        .from('pros')
        .select('matched')
        .eq('id', proId)
        .single()
      
      if (fetchError) {
        console.error(`Error fetching matched count for pro ${proId}:`, fetchError)
        continue
      }
      
      // Increment the matched count
      const currentCount = proData?.matched || 0
      const { error: updateError } = await client
        .from('pros')
        .update({ matched: currentCount + 1 })
        .eq('id', proId)
      
      if (updateError) {
        console.error(`Error updating matched count for pro ${proId}:`, updateError)
      }
    }
    
    return { success: true }
    
  } catch (error) {
    console.error('Unexpected error updating matching quiz:', error)
    return { success: false, error: 'An unexpected error occurred' }
  }
}